/**
 * The loan books in the words a banker outside lending uses (OR-30.28): "C&I" is business loans, "CRE" is commercial property loans. The catalog's own text keeps the codes; Decide shows it through
 * `plainCatalog`, so a desk that says "C&I growth" in one place never says "Business loans" in the next. Capitalised at the start of a line or sentence, lower case inside one.
 */
import type { ContentCatalog, DecisionCatalogEntry } from "@bsim/contracts";

const NAMES: Readonly<Record<string, string>> = { "C&I": "business loans", CRE: "commercial property loans" };
const CODES = /(?<![A-Za-z0-9&])(C&I|CRE)(?![A-Za-z0-9&])/g;
const STARTS = /(?:^|[.:!?·(“"]\s*)$/;

export function plainLoanNames(text: string): string {
  return text.replace(CODES, (code, _g: string, at: number) => {
    const name = NAMES[code] ?? code;
    return STARTS.test(text.slice(0, at)) ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : name;
  });
}

const note = (s: string | null | undefined): string | null | undefined => (s === null || s === undefined ? s : plainLoanNames(s));
const entryOf = (e: DecisionCatalogEntry): DecisionCatalogEntry => ({
  ...e, label: plainLoanNames(e.label), help: plainLoanNames(e.help), deferredCostNote: note(e.deferredCostNote) ?? null,
  options: Object.fromEntries(Object.entries(e.options).map(([sub, opts]) => [sub, opts.map((o) => ({ ...o, label: plainLoanNames(o.label), ...(o.consequence === undefined ? {} : { consequence: plainLoanNames(o.consequence) }) }))])),
  ...(e.deferredNotes === undefined ? {} : { deferredNotes: { up: plainLoanNames(e.deferredNotes.up), down: plainLoanNames(e.deferredNotes.down) } }),
  ...(e.context === undefined ? {} : { context: e.context.map((c) => ({ ...c, label: plainLoanNames(c.label) })) }),
  ...(e.subCaptions === undefined ? {} : { subCaptions: Object.fromEntries(Object.entries(e.subCaptions).map(([k, v]) => [k, plainLoanNames(v)])) }),
  ...(e.whatItDoes === undefined ? {} : { whatItDoes: plainLoanNames(e.whatItDoes) }),
  ...(e.tradeOffs === undefined ? {} : { tradeOffs: { now: plainLoanNames(e.tradeOffs.now), later: plainLoanNames(e.tradeOffs.later) } }),
  ...(e.businessCase === undefined ? {} : { businessCase: plainLoanNames(e.businessCase) }),
});

/** The catalog with its decisions' text in plain loan-book names; ids, values and everything else untouched. */
export const plainCatalog = (catalog: ContentCatalog): ContentCatalog => ({ ...catalog, decisions: catalog.decisions.map(entryOf) });
